/** A call script's shape: a plain string (one step) or structured steps plus an "If they say" list. */

export interface ScriptStep {
  title?: string | null;
  text: string;
}

export interface ScriptObjection {
  /** What the customer says ("I'm busy"). */
  cue: string;
  /** What the rep says back. */
  reply: string;
}

export interface StructuredScript {
  steps: ScriptStep[];
  if_they_say?: ScriptObjection[] | null;
}

export type CallScript = string | StructuredScript;

/** A value that renders as a script: a non-blank string, or an object carrying a `steps` array. */
export function isCallScript(value: unknown): value is CallScript {
  if (typeof value === "string") return value.trim().length > 0;
  return !!value && typeof value === "object" && Array.isArray((value as StructuredScript).steps);
}

/** The script as steps: a plain string is one untitled step; blank input is no steps. */
export function scriptSteps(script: CallScript | null | undefined): StructuredScript {
  if (script == null) return { steps: [] };
  if (typeof script === "string") return script.trim() ? { steps: [{ text: script }] } : { steps: [] };
  return {
    steps: script.steps.filter((s) => s.text.trim() || s.title?.trim()),
    if_they_say: (script.if_they_say ?? []).filter((o) => o.cue.trim() && o.reply.trim()),
  };
}

// A call mission keeps its "If they say" list inside each Phone step's brief, after this line, one
// `"cue" → reply` per line: the brief is the one script field a mission step has.
const IF_THEY_SAY = "If they say:";
const OBJECTION_LINE = /^"(.+)"\s*→\s*(.+)$/;

/** The step's words and the objections as one brief. */
export function briefWithObjections(text: string, objections: ScriptObjection[]): string {
  const kept = objections.filter((o) => o.cue.trim() && o.reply.trim());
  if (!kept.length) return text.trim();
  const lines = kept.map((o) => `"${o.cue.trim().replace(/"/g, "'")}" → ${o.reply.trim().replace(/\s+/g, " ")}`);
  return [text.trim(), "", IF_THEY_SAY, ...lines].join("\n");
}

// A call's words may read as three sections, each a header alone on its own line, naming the part of the call that
// follows it: Hello, Why, Book It (the templates' scripts) or Open, Offer, Close, in that order. The manager types the
// headers themselves, into the same brief field, the way they already type "If they say:" for objections.
const SECTION_SETS = [
  ["Hello", "Why", "Book It"],
  ["Open", "Offer", "Close"],
] as const;
const SECTION_HEADER_RE = /^(hello|why|book it|open|offer|close):?$/i;

/** `text` split into its three sections, in order, when every header of one of ``SECTION_SETS`` is its own line, in
 * that order, with no words before the first; null otherwise (today's single, untitled step). */
function sectionedSteps(text: string): ScriptStep[] | null {
  const lines = text.split("\n");
  const marks = lines
    .map((line, at) => ({ at, title: SECTION_HEADER_RE.exec(line.trim())?.[1] }))
    .filter((m): m is { at: number; title: string } => !!m.title);
  const titles = SECTION_SETS.find(
    (set) => marks.length === set.length && marks.every((m, i) => m.title.toLowerCase() === set[i].toLowerCase()),
  );
  if (!titles) return null;
  if (lines.slice(0, marks[0].at).some((l) => l.trim())) return null;
  return marks.map((m, i) => ({
    title: titles[i],
    text: lines
      .slice(m.at + 1, i + 1 < marks.length ? marks[i + 1].at : lines.length)
      .join("\n")
      .trim(),
  }));
}

/** A mission step's brief as a script: its words — as three sections (Hello / Why / Book It, or Open / Offer / Close) when it writes them, else
 * one step — and its "If they say" list when it has one. */
export function scriptFromBrief(brief: string | null | undefined): CallScript {
  const text = brief ?? "";
  if (!text.includes(`\n${IF_THEY_SAY}\n`)) {
    const steps = sectionedSteps(text.trim());
    return steps ? { steps } : text;
  }
  const split = briefAndObjections(text);
  if (!split) return text;
  return { steps: sectionedSteps(split.text) ?? [{ text: split.text }], if_they_say: split.objections };
}

/** The reverse of `briefWithObjections`: a mission step's words and its "If they say" list, or null when the brief
 * carries no such list. */
export function briefAndObjections(
  brief: string | null | undefined,
): { text: string; objections: ScriptObjection[] } | null {
  const text = brief ?? "";
  const at = text.lastIndexOf(`\n${IF_THEY_SAY}\n`);
  if (at < 0) return null;
  const lines = text.slice(at + IF_THEY_SAY.length + 2).split("\n");
  const objections = lines.map((l) => OBJECTION_LINE.exec(l.trim())).filter((m): m is RegExpExecArray => !!m);
  if (objections.length !== lines.filter((l) => l.trim()).length) return null;
  return { text: text.slice(0, at).trim(), objections: objections.map(([, cue, reply]) => ({ cue, reply })) };
}
